import { decryptDocument } from './decrypt';
import { assertVerified } from './envelope';
import { DocumentLockedError, KeyRefusedError } from './errors';
import { startHeartbeat } from './heartbeat';
import { EXPIRED } from './heartbeatDeadlines';
import { keyServiceDepsOf } from './keyApi';
import { requestKey } from './keyRequest';
import { PII_NOTICE_REQUIRED } from './refusalMessages';
import { withinKeyServiceTimeout } from './requestTimeout';

import type {
  AcceptedPiiNoticeVersion,
  KeyGrant,
  KeyServiceDeps,
  LockReason,
  OpenDeps,
  OpenedDocument,
  VerifiedEnvelope,
} from './types';

const UNCONFIRMED: LockReason = { kind: 'unreachable' };

function isPiiNoticeRefusal(err: unknown): boolean {
  return err instanceof KeyRefusedError && err.reason === PII_NOTICE_REQUIRED;
}

// Once the route hands the bytes to pdf.js they are detached here, moved to its worker, and fill()
// would throw. Only the route's pdf.js destroy() releases them then.
function wipe(bytes: Uint8Array): void {
  if (bytes.byteLength > 0) bytes.fill(0);
}

function requestKeyInTime(
  verified: VerifiedEnvelope,
  keyService: KeyServiceDeps,
  routeSignal: AbortSignal | undefined,
  piiNoticeVersion?: AcceptedPiiNoticeVersion,
): Promise<KeyGrant> {
  return withinKeyServiceTimeout(
    (signal) => requestKey(verified, keyService, { piiNoticeVersion, signal }),
    routeSignal,
  );
}

// An earlier acceptance never carries over (async README "Special PII Notice").
async function requestGrantWithPiiNotice(
  verified: VerifiedEnvelope,
  keyService: KeyServiceDeps,
  deps: OpenDeps,
): Promise<KeyGrant> {
  const { signal } = deps;
  try {
    return await requestKeyInTime(verified, keyService, signal);
  } catch (err) {
    if (!isPiiNoticeRefusal(err)) throw err;
  }
  const acceptedVersion = await deps.askPiiNotice();
  signal?.throwIfAborted();
  if (acceptedVersion === null) throw new KeyRefusedError(PII_NOTICE_REQUIRED);
  return requestKeyInTime(verified, keyService, signal, acceptedVersion);
}

export async function openDocument(
  verified: VerifiedEnvelope,
  deps: OpenDeps,
): Promise<OpenedDocument> {
  assertVerified(verified);
  deps.signal?.throwIfAborted();
  const keyService = keyServiceDepsOf(deps);
  const grant = await requestGrantWithPiiNotice(verified, keyService, deps);
  const grantExpiresAtWallMs = Date.now() + grant.session.secondsRemaining * 1000;

  let returnedToRoute = false;
  let ended = false;
  let lockedWhileOpening: LockReason | undefined;
  let bytes: Uint8Array | undefined;

  function end(): void {
    ended = true;
    heartbeat.stop();
    if (bytes) wipe(bytes);
  }
  function lock(why: LockReason): void {
    if (!returnedToRoute) {
      lockedWhileOpening ??= why;
      return;
    }
    if (ended) return;
    end();
    deps.onLock(new DocumentLockedError(why));
  }
  // Before the route has the document there is nothing to hide, so a suspend can only lock.
  function suspend(): void {
    if (!returnedToRoute) lock(UNCONFIRMED);
    else if (!ended) deps.onSuspend();
  }
  function resume(): void {
    if (returnedToRoute && !ended) deps.onResume();
  }

  const heartbeat = startHeartbeat(
    verified,
    grant.session,
    keyService,
    { onLock: lock, onSuspend: suspend, onResume: resume },
    deps.signal,
  );
  if (lockedWhileOpening) {
    heartbeat.stop();
    throw new DocumentLockedError(lockedWhileOpening);
  }
  try {
    bytes = await decryptDocument(verified, grant.dataKey);
  } catch (err) {
    heartbeat.stop();
    throw err;
  }
  if (deps.signal?.aborted) {
    end();
    throw deps.signal.reason;
  }
  // The heartbeat's expiry timer may not have fired yet, even with the time already up.
  if (Date.now() >= grantExpiresAtWallMs) lock(EXPIRED);
  if (lockedWhileOpening) {
    end();
    throw new DocumentLockedError(lockedWhileOpening);
  }
  returnedToRoute = true;
  return {
    bytes,
    docName: grant.docName,
    docScope: grant.docScope,
    viewerName: grant.viewerName,
    openedAt: grant.openedAt,
    sourceIp: grant.sourceIp,
    close: end,
  };
}
